import { getConfig } from "./dialogConfig.js";
import { buildHandlers } from "./dialogHandlerBuilder.js";

import { setActiveDialog } from "../../../../../app/activeStateManager.js";
import { currentTable, currentTableName } from "../../../../../app/viewManager.js";
import { restoreCascadeFilters } from "../dropdowns/dropdownManager.js";

let dialog = null;
let currentName = null;
let currentCloseHandler = null;
let currentBackHandler = null;
let switchingDialog = false;

export const getCurrentDialogName = () => currentName;

const fieldStoragePrefix = 'df_';
const recordKey = 'df__recordId';
const placeholdersKey = 'df__placeholders';

function saveRecordId(id) {
    if (id === undefined || id === null) { sessionStorage.removeItem(recordKey); return; }
    sessionStorage.setItem(recordKey, String(id));
}

function getStoredRecordId() {
    const raw = sessionStorage.getItem(recordKey);
    if (raw === null) return null;
    const num = Number(raw);
    return raw !== '' && Number.isFinite(num) ? num : raw;
}

function savePlaceholders(placeholders) {
    if (!placeholders || !Object.keys(placeholders).length) { sessionStorage.removeItem(placeholdersKey); return; }
    sessionStorage.setItem(placeholdersKey, JSON.stringify(placeholders));
}

function getStoredPlaceholders() {
    try {
        const placeholders = JSON.parse(sessionStorage.getItem(placeholdersKey));
        return placeholders && typeof placeholders === 'object' && !Array.isArray(placeholders) ? placeholders : null;
    } catch {
        return null;
    }
}

function getFieldElements() {
    if (!dialog) return [];
    return [...dialog.shadowRoot.querySelectorAll('.dialogInput[id]')];
}

function saveFieldsToStorage() {
    getFieldElements().forEach(element => {
        const key = fieldStoragePrefix + element.id;
        const value = element.tagName === 'APP-INPUT' ? element.getValue() : element.value;
        if (value === null || value === undefined || value === '') { sessionStorage.removeItem(key); return; }
        if (Array.isArray(value)) {
            if (value.length > 0) sessionStorage.setItem(key, value.join(','));
            else sessionStorage.removeItem(key);
        } else if (typeof value === 'object') {
            const { from, to } = value;
            if (from || to) sessionStorage.setItem(key, `${from ?? ''},${to ?? ''}`);
            else sessionStorage.removeItem(key);
        } else {
            sessionStorage.setItem(key, String(value));
        }
    });
}

function restoreFieldsFromStorage() {
    getFieldElements().forEach(element => {
        const raw = sessionStorage.getItem(fieldStoragePrefix + element.id);
        if (raw === null) return;
        if (element.tagName === 'APP-INPUT') {
            const type = element.getAttribute('type') || 'dropdown';
            let restoreVal;
            if (element.hasAttribute('multiple')) {
                restoreVal = raw.split(',').filter(Boolean);
            } else if (type === 'daterange' || type === 'timerange') {
                const [from, to] = raw.split(',');
                restoreVal = { from: from || null, to: to || null };
            } else {
                restoreVal = raw;
            }
            element.setValue(restoreVal, { silent: true });
        } else {
            element.value = raw;
        }
    });
}

function clearFieldStorage() {
    getFieldElements().forEach(element => sessionStorage.removeItem(fieldStoragePrefix + element.id));
    sessionStorage.removeItem(recordKey);
    sessionStorage.removeItem(placeholdersKey);
}

function setDialogHash(name) {
    const hash = window.location.hash;
    const clean = hash.replace(/^#\/?/, '');
    const questionIdx = clean.indexOf('?');
    const pathPart = questionIdx >= 0 ? clean.slice(0, questionIdx) : clean;
    const queryStr  = questionIdx >= 0 ? clean.slice(questionIdx + 1) : '';
    const params = new URLSearchParams(queryStr);

    if (name) params.set('dialog', name);
    else params.delete('dialog');

    history.replaceState(history.state, '', `#${pathPart}?${params.toString()}`);
}

function getOrCreateDialog() {
    if (!dialog || !document.contains(dialog)) {
        dialog = document.createElement("app-dialog");
        dialog.addEventListener("close", () => {
            currentName = null;
            setActiveDialog(null);
            if (!switchingDialog) setDialogHash(null);
            clearFieldStorage();
            currentCloseHandler?.();
            currentCloseHandler = null;
        });
        dialog.addEventListener("back",  () => currentBackHandler?.());
        dialog.addEventListener("input-change", saveFieldsToStorage);
        dialog.addEventListener("change",       saveFieldsToStorage);
        document.addEventListener("dialog-cascade-complete", saveFieldsToStorage);
    }
    return dialog;
}

window.addEventListener('restore-dialog', ({ detail }) => openDialog(detail.name, null, false, detail.filterParams));

export async function openDialog(dialogName = currentName, data = null, isTask = false, restoreParams = null) {
    if (["create", "edit", "delete"].includes(dialogName)) {
        const name = currentTable?.tableName ?? currentTableName;
        if (!name) return;
        dialogName = `${dialogName}${name.charAt(0).toUpperCase() + name.slice(1)}`;
    }

    if (dialogName === currentName) { closeDialog(); return; }
    if (currentName) {
        switchingDialog = true;
        closeDialog();
        switchingDialog = false;
    }

    const dlg = getOrCreateDialog();

    if (restoreParams) {
        if (data?.id === undefined) {
            const storedId = getStoredRecordId();
            if (storedId !== null) data = { ...data, id: storedId };
        }
    } else {
        saveRecordId(data?.id);
    }

    const handlers = await buildHandlers(dialogName, data, isTask);
    if (restoreParams) {
        const storedPlaceholders = getStoredPlaceholders();
        if (storedPlaceholders && handlers.placeholders) handlers.placeholders = storedPlaceholders;
    } else {
        savePlaceholders(handlers.placeholders);
    }
    const config   = getConfig(dialogName, handlers, isTask);
    if (!config) return;

    currentCloseHandler = handlers.onClose ?? null;
    currentBackHandler = handlers.onBack ?? null;
    currentName = dialogName;
    setActiveDialog(dialogName);

    await dlg.open(config);

    if (handlers.setupPromise) await handlers.setupPromise;
    if (handlers.setup)        await handlers.setup();

    if (restoreParams) {
        restoreFieldsFromStorage();
        await restoreCascadeFilters();
        if (handlers.restore) await handlers.restore();
    } else {
        saveFieldsToStorage();
    }

    if (!handlers.skipHash) setDialogHash(currentName);
}

export function closeDialog() {
    if (!dialog) return;
    dialog.close();
    currentName = null;
    setActiveDialog(null);
    if (!switchingDialog) setDialogHash(null);
}

document.addEventListener("change", event => {
    if (event.target.matches("select.dialogInput") && event.target.value)
        event.target.style.borderColor = "";
});